import React, { useState, useLayoutEffect, useRef } from 'react';
import { IntlProvider as ReactIntlProvider } from 'react-intl';
import {
  IntlProvider as ReactIntlNextProvider,
  type MessageFormatElement
} from 'react-intl-next';

import { getBrowserDefaultLocale } from '../constants';
import { fetchIntlMessages, getResolvedIntlMessages } from '../fetch-intl-messages';

export interface IntlProviderWithMessagesProps {
  children: React.ReactNode;
  locale: string;
}

export const IntlProviderWithMessages: React.FunctionComponent<
  IntlProviderWithMessagesProps
> = (props) => {
  const { children, locale } = props;
  const [localeMessages, setLocaleMessages] = useState<
    Record<string, string> | Record<string, MessageFormatElement[]> | undefined
  >(undefined);

  const isFirstRender = useRef(true);

  /**
   * This skips fetching on initial render and only fetches when the locale changes.
   * However, if there are no resolved messages from the fetch during bootstrap,
   * this will force a refetch.
   */
  useLayoutEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      const resolvedMessages = getResolvedIntlMessages(locale);

      if (resolvedMessages) {
        setLocaleMessages(resolvedMessages);

        return;
      }
    }

    const fetchAndSetMessages = async () => {
      const messages = await fetchIntlMessages(locale);

      setLocaleMessages(messages);
    };

    fetchAndSetMessages();
  }, [locale]);

  if (localeMessages === undefined) {
    return null;
  }

  return (
    <ReactIntlProvider locale={locale}>
      <ReactIntlNextProvider
        locale={locale}
        defaultLocale={getBrowserDefaultLocale()}
        messages={localeMessages}
        defaultRichTextElements={{
          code: (chunks) => <code>{chunks}</code>,
          br: () => <br />,
          // we show this on a dark background, so we can't use text primitives today
          // because they don't support inverting the color
          // eslint-disable-next-line @atlaskit/design-system/use-primitives-text
          strong: (chunks) => <strong>{chunks}</strong>
        }}
        wrapRichTextChunksInFragment
      >
        {children}
      </ReactIntlNextProvider>
    </ReactIntlProvider>
  );
};
